import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { aiDraftMessages, samplePlan, samplePlanItems, getPlanBundle } from './planData';

const itemTypeOrder = ['goal', 'objective', 'activity', 'intervention'];

const PlanWorkspacePage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [discussionInput, setDiscussionInput] = useState('');
  const [discussionMessages, setDiscussionMessages] = useState([
    { id: 'm-1', from: 'Clinical Trial Management Team', to: 'Patient + Participants', text: 'AI-assisted plan draft is ready for collaborative refinement across goals, objectives, activities, and interventions.', time: 'Today, 9:40 AM' }
  ]);

  const planBundle = useMemo(() => {
    return getPlanBundle(planId || samplePlan.id);
  }, [planId]);

  const currentPlan = planBundle?.plan || samplePlan;
  const currentItems = planBundle?.items || samplePlanItems;

  const hierarchyByType = useMemo(() => {
    return itemTypeOrder.map((type) => ({
      type,
      items: currentItems.filter((item) => item.type === type)
    }));
  }, [currentItems]);

  const handlePostDiscussion = () => {
    const text = discussionInput.trim();
    if (!text) {
      return;
    }
    setDiscussionMessages((prev) => [...prev, {
      id: `m-${Date.now()}`,
      from: 'Patient',
      to: 'Participants',
      text,
      time: 'Just now'
    }]);
    setDiscussionInput('');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1240, mx: 'auto' }}>
      <Button variant="text" sx={{ mb: 2, fontWeight: 600 }} onClick={() => navigate('/dashboard')}>
        ← Back to Trial Planning
      </Button>

      <Card sx={{ mb: 3, borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 0 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" gutterBottom>{currentPlan.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Patient-need-centric shared care planning for trial participation.
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => navigate('/plans/new')}>
              Create Plan (AI Wizard)
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Created by: {currentPlan.createdBy}</Typography>
              <Typography variant="body2" color="text.secondary">Managed by: {currentPlan.managedBy}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Chip label={`Approval: ${currentPlan.approvalStatus}`} color="primary" variant="outlined" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 0 }}>
        <CardContent>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            <Tab label="Plan Items" />
            <Tab label="Discussion" />
            <Tab label="Participants" />
          </Tabs>

          {activeTab === 0 && (
            <Grid container spacing={2}>
              {hierarchyByType.map((group) => (
                <Grid item xs={12} md={6} key={group.type}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize', mb: 1 }}>{group.type}</Typography>
                      <List dense>
                        {group.items.map((item) => (
                          <ListItem key={item.id} disablePadding>
                            <ListItemButton onClick={() => navigate(`/plans/${currentPlan.id}/items/${item.type}/${item.id}`)}>
                              <ListItemText
                                primary={item.name}
                                secondary={`Type: ${item.type} • Status: ${item.approvalStatus}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>AI-generated draft messages (editable)</Typography>
              <List dense sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                {aiDraftMessages.map((msg, index) => (
                  <ListItem key={`ai-${index}`}>
                    <ListItemText primary={msg} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Post a discussion comment"
                value={discussionInput}
                onChange={(e) => setDiscussionInput(e.target.value)}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button variant="contained" onClick={handlePostDiscussion} disabled={!discussionInput.trim()}>
                  Post Comment
                </Button>
              </Box>
              <List sx={{ mt: 2 }}>
                {discussionMessages.map((m) => (
                  <ListItem key={m.id} alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemText
                      primary={`${m.from} → ${m.to}`}
                      secondary={`${m.text} • ${m.time}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {activeTab === 2 && (
            <FormGroup>
              {currentPlan.participants.map((participant) => (
                <FormControlLabel key={participant} control={<Checkbox checked />} label={participant} />
              ))}
            </FormGroup>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PlanWorkspacePage;
